import {
  onlineManager,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { type RouterOutputs, useTRPC } from '../../lib/trpc'
import { useToast } from '../../ui/Toast'
import { useConfirm } from '../../ui/useConfirm'
import { ABLE, AccessGroups, DOES, groupLines } from './AccessGroups'
import { folderTree, type Movable } from './folder-tree'
import { nameOf } from './people'

/**
 * A space's folders, read as a tree. Unfiled is a folder on the server and its
 * own entry here, never a branch of the tree: nothing nests in it and it is
 * not moved.
 */
export function useFolderTree(spaceId: string | null) {
  const trpc = useTRPC()
  const folders = useQuery({
    ...trpc.folders.list.queryOptions({ spaceId: spaceId ?? undefined }),
    enabled: !!spaceId,
  })
  const tree = useMemo(
    () =>
      folderTree(
        (folders.data ?? []).filter((f) => !f.unfiled),
        spaceId,
      ),
    [folders.data, spaceId],
  )
  return { folders, tree }
}

/**
 * A space's folders, read as a tree, and the one way every surface files a
 * document or moves a folder: the move is sent, the toast says where it went and
 * offers it back, and both lists are read again whatever the answer — a move
 * whose reply was lost may still have landed.
 *
 * A move that would let anybody further in, through a share on the folder it
 * goes into or one above it, names them and asks first; the server refuses it
 * otherwise.
 *
 * Offline it is refused outright. A paused mutation would leave the card where
 * it was with no word, and a second drag would queue a second move to replay.
 */
export function useFolderMoves(spaceId: string | null) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const toast = useToast()
  const { confirm, dialog } = useConfirm()
  const { folders, tree } = useFolderTree(spaceId)
  const file = useMutation(trpc.folders.file.mutationOptions())
  const moveFolder = useMutation(trpc.folders.move.mutationOptions())

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries(trpc.documents.list.queryFilter()),
      qc.invalidateQueries(trpc.folders.list.queryFilter()),
    ])

  const send = (item: Movable, target: string | null, allowSharing: boolean) =>
    item.kind === 'folder'
      ? moveFolder.mutateAsync({
          spaceId: spaceId ?? undefined,
          id: item.id,
          parentId: target,
          allowSharing,
        })
      : file.mutateAsync({
          spaceId: spaceId ?? undefined,
          kind: item.kind,
          id: item.id,
          folderId: target,
          allowSharing,
        })

  const where = (item: Movable, target: string | null) =>
    target
      ? (tree.byId.get(target)?.name ?? 'the folder')
      : item.kind === 'folder'
        ? 'the top level'
        : 'Unfiled'

  /* Asked at the moment of the move, never from a cache: a share made a
     minute ago is exactly the one the person needs to hear about. Null is a
     move declined; otherwise whether the person agreed to anybody it lets
     further in, which the server asks to be told. */
  const mayShare = async (
    item: Movable,
    target: string | null,
  ): Promise<{ allowSharing: boolean } | null> => {
    const gained = await qc.fetchQuery({
      ...trpc.folders.widens.queryOptions({
        spaceId: spaceId ?? undefined,
        kind: item.kind,
        id: item.id,
        to: target,
      }),
      staleTime: 0,
    })
    if (gained.length === 0) return { allowSharing: false }
    const someone =
      gained.length === 1 ? 'one more person' : `${gained.length} more people`
    const agreed = await confirm({
      title: gained.every((g) => g.had === null)
        ? `Share ${item.name} with ${someone}?`
        : `Let ${gained.length === 1 ? 'somebody' : `${gained.length} people`} further into ${item.name}?`,
      body: (
        <MoveShares item={item} where={where(item, target)} gained={gained} />
      ),
      verb: 'Move and share',
      destructive: false,
    })
    return agreed ? { allowSharing: true } : null
  }

  /** Ask if the move lets anybody further in, then send it; false if it was not sent. */
  const sendAsked = async (
    item: Movable,
    target: string | null,
    failed: string,
  ) => {
    if (!onlineManager.isOnline()) {
      toast(`You’re offline, so ${item.name} ${failed}.`, 'danger')
      return false
    }
    try {
      const asked = await mayShare(item, target)
      if (!asked) return false
      await send(item, target, asked.allowSharing)
      return true
    } catch (e: unknown) {
      toast(
        e instanceof Error ? e.message : `${item.name} ${failed}.`,
        'danger',
      )
      return false
    } finally {
      void refresh()
    }
  }

  const move = async (item: Movable, target: string | null) => {
    const from = item.kind === 'folder' ? item.parentId : item.folderId
    if (!(await sendAsked(item, target, 'wasn’t moved'))) return
    toast(`Moved ${item.name} to ${where(item, target)}.`, 'success', {
      label: 'Undo',
      /* Back where it was can still let somebody in: the folder it left may
         have been shared since. So Undo asks as any move does. */
      onSelect: () => {
        void sendAsked(item, from, 'wasn’t moved back').then(
          (sent) => sent && toast(`Moved ${item.name} back.`),
        )
      },
    })
  }

  return { folders, tree, move, dialog }
}

type Gained = RouterOutputs['folders']['widens'][number]

/** Who a move lets further in, grouped under the shared folder that does it. */
function MoveShares({
  item,
  where,
  gained,
}: {
  item: Movable
  where: string
  gained: Gained[]
}) {
  return (
    <div className="move-shares">
      <p className="move-shares__lede">
        Moving {item.name} to {where} puts it in a folder shared with{' '}
        {gained.length === 1 ? 'this person' : 'these people'}, so they can do
        more with it at once
        {item.kind === 'folder' ? ', and with everything filed in it' : ''}.
        They are not told.
      </p>
      <AccessGroups
        groups={groupLines(
          gained,
          (g) => ({ key: g.folder.id, heading: `Through ${g.folder.name}` }),
          (g) => ({
            personId: g.personId,
            name: nameOf(g),
            change: g.had
              ? `Can ${ABLE[g.gains]} it; ${DOES[g.had]} it now`
              : `Can ${ABLE[g.gains]} it`,
          }),
        )}
      />
    </div>
  )
}
